import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { api } from '../../api'
import { useT } from '../../i18n'
import { addAdvertisement, fetchAdvertisements, updateAdvertisement } from '../../redux/actions/advertisement.actions'
import { StoreState } from '../../redux/reducers'
import { Advertisement, AdvertisementStatus } from '../../redux/reducers/advertisement.reducer'
import { AppDispatch } from '../../redux/store'
import { toString } from '../../utils'
import { ToastType, useToast } from '../ToastProvider'
import ModalWithHeader from '../modals/ModalWithHeader'
import FormInput from '../utils/FormInput'
import Switch from '../utils/Switch'

interface AdvertisementFormProps {
    onClose?: () => void
    id?: string | null
}

const AdvertisementForm = (props: AdvertisementFormProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const toast = useToast()
    const t = useT()

    const advertisement = useSelector<StoreState, Advertisement | null>(state =>
        props.id ? state.advertisements.find(item => item.id === props.id) || null : null
    )

    const [image, setImage] = React.useState<string>('')
    const [imageError, setImageError] = React.useState<string>('')
    const [link, setLink] = React.useState<string>('')
    const [linkError, setLinkError] = React.useState<string>('')
    const [status, setStatus] = React.useState<AdvertisementStatus>('active')
    const [uploading, setUploading] = React.useState<boolean>(false)
    const [loading, setLoading] = React.useState<boolean>(false)

    const LinkChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setLink(e.target.value)
        setLinkError('')
    }

    const ImageUploadHandler: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0]
        if (!file) {
            return
        }

        const formData = new FormData()
        formData.append('file', file)
        setUploading(true)

        try {
            const response = await api.post<{ data: string }>('upload-img/advertisement', formData)
            if (response.status === 200 && response.data.data) {
                setImage(response.data.data)
                setImageError('')
            }
        } catch (error) {
            toast(t('Unable to upload image'), ToastType.ERROR)
        } finally {
            setUploading(false)
            e.target.value = ''
        }
    }

    const submitHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
        e.preventDefault()

        let error = false

        if (image === '') {
            setImageError(t('Image required'))
            error = true
        }

        if (link === '') {
            setLinkError(t('Link required'))
            error = true
        }

        if (error) {
            return
        }

        const data = {
            image: toString(image),
            link: toString(link),
            status
        }

        setLoading(true)

        const request = props.id && advertisement
            ? dispatch(updateAdvertisement(data, advertisement.id))
            : dispatch(addAdvertisement(data))

        request.then(() => {
            toast(props.id ? t('Advertisement updated') : t('Advertisement added'))
            dispatch(fetchAdvertisements())
            props.onClose?.()
        }).catch(text => {
            toast(text, ToastType.ERROR)
        }).finally(() => {
            setLoading(false)
        })
    }

    useEffect(() => {
        if (advertisement && props.id) {
            setImage(advertisement.image)
            setLink(advertisement.link)
            setStatus(advertisement.status)
        }
    }, [advertisement, props.id])

    return <ModalWithHeader
        buttonText={t('Save')}
        headerText={props.id ? t('Edit Advertisement') : t('Add Advertisement')}
        loading={loading || uploading}
        onClose={props.onClose}
        onSave={submitHandler}
    >
        <div className='row'>
            <div className='col-12 mb-4'>
                <label className='form-label'>{t('Image')}</label>
                <input
                    type='file'
                    accept='image/*'
                    className='form-control'
                    onChange={ImageUploadHandler}
                />
                {imageError && <div className='text-danger small mt-1'>{imageError}</div>}
                {image && (
                    <img src={image} alt={t('Advertisement')} className='mt-3 rounded' style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'cover' }} />
                )}
            </div>
            <div className='col-12'>
                <FormInput
                    type='text'
                    label={t('Redirect link')}
                    placeholder='https://example.com or /shop/shopId'
                    errorText={linkError}
                    value={link}
                    onChange={LinkChangeHandler}
                    containerClass='mb-4'
                />
            </div>
            <div className='col-12 mb-2'>
                <Switch
                    label={status === 'active' ? t('Active') : t('Inactive')}
                    checked={status === 'active'}
                    onClick={() => setStatus(status === 'active' ? 'inactive' : 'active')}
                />
            </div>
        </div>
    </ModalWithHeader>
}

export default AdvertisementForm
